import type { BlogArticle } from '@/lib/blog-data'
import { PUBLIC_SOUND_COUNT_LABEL } from '@/lib/public-sound-count'

export const post: BlogArticle = {
    slug: 'tesla-lock-sound-volume',
  image: '/images/blog/generated/tesla-lock-sound-volume.png',
    title: 'Tesla Lock Sound Volume: How to Make It Louder or Quieter',
    excerpt: 'Tesla lock sound too loud or too quiet? What controls the volume of a custom LockChime.wav, how to raise or lower it with the free converter, and what to check first.',
    author: 'Tesla Lock Sound Team',
    publishedAt: '2026-05-10',
    category: 'tips-tricks',
    tags: [
      'volume',
      'tesla lock sound volume',
      'lockchime-wav',
      'decibels',
      'normalize audio',
      'troubleshooting',
      'lock sound requirements',
      'audacity',
    ],
    readingTime: 7,
    featured: false,
    content: `
# Tesla Lock Sound Volume: How to Make It Louder or Quieter

Volume is a common complaint about custom lock sounds: either the chime is hard to hear from a distance, or it is startlingly loud in a quiet parking garage. This guide covers what sets the volume of a custom \`LockChime.wav\`, what we do and don't know about it, and how to raise or lower it.

## What sets the volume of a custom lock sound

Two things: the level of the audio file, and whatever Tesla's software does to it before it reaches the external speaker. The file is the part you control. Tesla does not publish a loudness target or a decibel figure for custom lock sounds, so this page does not give one. Any "right dB range" you read elsewhere, including on earlier versions of this page, is not something we can source.

What is documented: the hard requirement is a WAV file named exactly \`LockChime.wav\`, and the recommended spec is 44.1 kHz, 16-bit, 1–5 seconds, under 1 MB. Those are best-practice values for clean playback, not a published Tesla volume spec.

## Why files differ in loudness

Sounds come from many sources, so they are recorded and exported at different levels. A clip that sounds fine in headphones can be quiet through a small outdoor speaker, and a clip with one very loud peak can sound quiet overall because the peak limits how far the level can be raised. Library files are not all at the same level, so compare a couple of sounds by ear before you decide a file is the problem.

## How to make a Tesla lock sound louder

### Raise the level with the converter

The free [audio converter](/convert) runs in your browser (nothing is uploaded) and has two loudness controls:

1. Upload your sound and trim it to 5 seconds or less
2. Set the loudness preset to **Loud** (the presets are Quiet, Normal and Loud)
3. If you want more, raise the fine-tune gain. It goes up to +6 dB
4. Export. You get a \`LockChime.wav\` at 44.1 kHz, 16-bit

The converter keeps peaks at or below -1 dBFS, so it will not clip. The flip side: a very short, peaky sound may not get much louder, because its peak is already close to that ceiling. If a sound is still quiet after the Loud preset, try a different sound.

### Check the install

A quiet lock sound is sometimes not a volume problem at all. If the car fell back to the factory chime, re-select USB under Toybox → Boombox → Lock Sound. Check that the file is \`Boombox/LockChime.wav\` on a FAT32 or exFAT drive in a USB port that carries data (the glovebox port on newer cars). The [quiet-after-an-update guide](/blog/fix-quiet-tesla-lock-sound-2025-45) walks through these checks and links to owner reports about volume changes after updates.

### Try a different sound

Preview a few sounds from the [library](/sounds) and compare them. Sharp, short sounds are easier to hear outdoors than long, soft ones.

## How to make a Tesla lock sound quieter

### Lower the level with the converter

1. Upload the sound to the [converter](/convert)
2. Set the loudness preset to **Quiet**
3. Lower the fine-tune gain if you want it softer still. The gain slider goes down to -30 dB
4. Export, copy the new \`LockChime.wav\` to \`Boombox/\` on your USB drive, and test from where you normally park

Change it in small steps and test after each one. There is no single right amount.

### Choose a naturally quieter sound

Softer sounds (a single bell note, a short chime) tend to be less startling than bass-heavy or percussive ones. See our [quiet lock sounds](/blog/best-quiet-tesla-lock-sounds).

### In-car volume

We have not found Tesla documentation for a dedicated lock-sound volume setting. Owners in the forum threads cited in the [quiet-after-an-update guide](/blog/fix-quiet-tesla-lock-sound-2025-45) describe it as fixed by the car, with one owner suggesting the touchscreen volume while a Boombox sound plays may also affect the lock sound; that was not confirmed. If your car shows a setting for external speaker volume, it is worth trying, but we can't tell you where it is on each software version.

## Frequently Asked Questions

**What dB should my Tesla lock sound be?**
Tesla does not publish a dB target for custom lock sounds, and we can't give you a number we could back up. Use the converter's Loud preset if the sound is too quiet or its Quiet preset if it is too loud, then adjust the gain by ear.

**How do I make my Tesla lock sound louder?**
Re-export the file with the [converter](/convert): choose the Loud preset and add up to +6 dB of fine-tune gain if needed.

**Why did my lock sound volume change after a software update?**
Owners report this after some updates, but we have not found a Tesla statement explaining it. See the [quiet-after-an-update guide](/blog/fix-quiet-tesla-lock-sound-2025-45) for the sources and the checks to run.

**Why does my custom lock sound seem quieter than the stock chime?**
The level of your file is part of it: a clip recorded or exported at a low level will play quietly. Raising it in the converter is the practical fix.

**What format does a custom lock sound need?**
A WAV file named \`LockChime.wav\`. We recommend 44.1 kHz, 16-bit, 1–5 seconds, under 1 MB.

---

Browse ${PUBLIC_SOUND_COUNT_LABEL} sounds in the [library](/sounds). Every download is a \`LockChime.wav\` ready for the USB drive. If a sound is still too quiet or too loud, the [audio converter](/convert) lets you change its level. For more troubleshooting, see [Tesla lock sound too quiet](/blog/tesla-lock-sound-too-quiet).
`,
  }
